/**
 * Flow Matrix — a strike ladder with the call and put side of one metric, side by side.
 *
 * The previous matrix downloaded the whole option chain for every column no matter which DTE you
 * picked, because `expiry_range` was ignored on both backends. With that fixed (1DTE on SPY is now
 * ~700KB against 23MB), the view can be built the other way round: keep the payload small, cache
 * what you have, and quietly warm the selections the user is most likely to make next — hovering a
 * symbol or DTE chip prefetches it, so the click is usually instant.
 *
 * Everything on screen is measured, never inferred: strikes come from the chain, the spot marker
 * from the underlying price on those rows, and an empty window (0DTE on a day with no expiry)
 * renders as empty rather than silently widening to a window that would answer a different question.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './RtlScannerView.css';
import './FlowMatrixView.css';
import './FlowMatrixView.ds.css';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import ToolState from './mmec/ToolState';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import { LanguageContext } from '../contexts/LanguageContext';
import FlowMatrixCompare, { CompareState, loadCompareState, newPaneId, saveCompareState } from './FlowMatrixCompare';
import { ASSET_CLASS_OPTIONS, AssetClass, classifyTicker } from '../utils/assetClass';
import {
  atmIndex,
  fetchLadder,
  MATRIX_DTE_OPTIONS,
  MATRIX_METRIC_BY_ID,
  MATRIX_STRIKE_OPTIONS,
  MatrixLadder,
  MatrixQuery,
  metricPeak,
  metricsForSource,
  metricTotals,
  peekLadder,
  prefetchLadder,
} from '../utils/flowMatrixApi';

/** Symbols offered as one-click chips; anything else can be typed. */
const QUICK_SYMBOLS = ['SPY', 'QQQ', 'IWM', 'NVDA', 'TSLA', 'AAPL', 'AMZN', 'META', 'AMD', 'MSFT'];

const REFRESH_MS = 60000;

/** Read by capture scripts too: keep the shape {symbol, dte, strikeRange, metric}. */
const PREFS_KEY = 'mmecFlowMatrixPrefs';

/** The single ladder reads the stable chain, so only its metrics are offered here. */
const SINGLE_METRICS = metricsForSource('stable');

interface Prefs {
  symbol: string;
  dte: string;
  strikeRange: string;
  metric: string;
}

const DEFAULT_PREFS: Prefs = {
  symbol: 'SPY',
  dte: 'weekly',
  strikeRange: '20',
  metric: 'volume',
};

function loadPrefs(): Prefs {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw);
    return {
      symbol: typeof p.symbol === 'string' && p.symbol ? p.symbol : DEFAULT_PREFS.symbol,
      dte: typeof p.dte === 'string' && p.dte ? p.dte : DEFAULT_PREFS.dte,
      strikeRange: typeof p.strikeRange === 'string' && p.strikeRange ? p.strikeRange : DEFAULT_PREFS.strikeRange,
      metric: SINGLE_METRICS.some((m) => m.id === p.metric) ? p.metric : DEFAULT_PREFS.metric,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function relativeAge(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
}

export interface FlowMatrixViewProps {
  simpleLayout?: boolean;
}

interface FlowMatrixSingleProps extends FlowMatrixViewProps {
  onCompare: (current: Prefs) => void;
}

const FlowMatrixSingle: React.FC<FlowMatrixSingleProps> = ({ simpleLayout = false, onCompare }) => {
  const isArabic = (useContext(LanguageContext)?.language ?? 'en') === 'ar';
  const initial = useRef<Prefs>(loadPrefs());
  const [symbol, setSymbol] = useState(initial.current.symbol);
  const [symbolInput, setSymbolInput] = useState(initial.current.symbol);
  const [dte, setDte] = useState(initial.current.dte);
  const [strikeRange, setStrikeRange] = useState(initial.current.strikeRange);
  const [metric, setMetric] = useState(initial.current.metric);
  /** Narrows the quick-pick list; the ladder itself is always one underlying. */
  const [quickClass, setQuickClass] = useState<AssetClass | ''>('');

  const query: MatrixQuery = useMemo(() => ({ symbol, dte, strikeRange }), [symbol, dte, strikeRange]);

  // Paint from cache immediately, then refresh — switching back to a symbol should never blank out.
  const [ladder, setLadder] = useState<MatrixLadder | null>(() => peekLadder(query));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const { openHistorical } = useHistoricalModal();

  useEffect(() => {
    try {
      window.localStorage.setItem(PREFS_KEY, JSON.stringify({ symbol, dte, strikeRange, metric }));
    } catch {
      /* preferences simply do not persist */
    }
  }, [symbol, dte, strikeRange, metric]);

  const load = useCallback(
    async (q: MatrixQuery, background: boolean) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      const cached = peekLadder(q);
      if (cached) setLadder(cached);
      if (!background && !cached) setLoading(true);
      setError(null);
      try {
        const next = await fetchLadder(q, controller.signal);
        if (!controller.signal.aborted) setLadder(next);
      } catch (e) {
        if (controller.signal.aborted || (e as Error)?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Could not load the chain');
        if (!cached) setLadder(null);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    load(query, false);
    const timer = window.setInterval(() => load(query, true), REFRESH_MS);
    return () => {
      window.clearInterval(timer);
      abort.current?.abort();
    };
  }, [query, load]);

  // Warm the neighbouring windows for the current symbol: the next click is usually one of these.
  useEffect(() => {
    const t = window.setTimeout(() => {
      MATRIX_DTE_OPTIONS.forEach((o) => {
        if (o.value !== dte) prefetchLadder({ symbol, dte: o.value, strikeRange });
      });
    }, 600);
    return () => window.clearTimeout(t);
  }, [symbol, dte, strikeRange]);

  // Anchor the ladder at the money on first paint of a new chain.
  useEffect(() => {
    if (!ladder || !bodyRef.current) return;
    const idx = atmIndex(ladder);
    if (idx < 0) return;
    const row = bodyRef.current.querySelector<HTMLElement>(`[data-row-index="${idx}"]`);
    if (row) row.scrollIntoView({ block: 'center' });
  }, [ladder]);

  const def = MATRIX_METRIC_BY_ID[metric] ?? SINGLE_METRICS[0];
  const peak = ladder ? metricPeak(ladder, metric) : 0;
  const totals = ladder ? metricTotals(ladder, metric) : null;
  const atm = ladder ? atmIndex(ladder) : -1;

  const applySymbol = (next: string) => {
    const s = next.trim().toUpperCase();
    if (!s) return;
    setSymbol(s);
    setSymbolInput(s);
  };

  const openContract = (strike: number, expiry: string, side: 'call' | 'put') => {
    if (!expiry) return;
    openHistorical(
      {
        underlying: symbol,
        strike,
        expiry,
        optionType: side === 'put' ? 'PUT' : 'CALL',
        source: metric === 'oi' ? 'oi' : 'volume',
      },
      { compactChrome: simpleLayout },
    );
  };

  /** Bar width as a share of the ladder's peak, so both sides read on one scale. */
  const barPct = (v: number) => (peak > 0 ? Math.min(100, (Math.abs(v) / peak) * 100) : 0);

  const ds = isDesignSystemOn();
  return (
    <div className={`rtlscan flowmatrix${simpleLayout ? ' simple' : ''}${ds ? ' mmec' : ''}`}>
      <div className="head">
        <h1>
          Flow Matrix <span>· {symbol}</span>
        </h1>
        <div className="hr">
          {ladder && ladder.spot > 0 && (
            <span className="status">
              spot <b>{ladder.spot.toFixed(2)}</b>
            </span>
          )}
          <span className="status">
            {loading && !ladder
              ? 'Loading chain…'
              : ladder
                ? `${ladder.rows.length} strikes · ${ladder.expiries.length} expir${ladder.expiries.length === 1 ? 'y' : 'ies'} · ${relativeAge(ladder.fetchedAt)}`
                : '—'}
          </span>
          <button
            type="button"
            className="chip fmcompare"
            title={
              isArabic
                ? 'قارن رموزًا أو مجموعات بيانات مختلفة جنبًا إلى جنب'
                : 'Compare different underlyings or data sets side by side'
            }
            onClick={() => onCompare({ symbol, dte, strikeRange, metric })}
          >
            + {isArabic ? 'مقارنة' : 'Compare'}
          </button>
        </div>
      </div>

      <div className="bar">
        <div className="fg">
          <span className="lab">Symbol</span>
          <div className="ctl">
            <input
              className="num mono search"
              type="text"
              value={symbolInput}
              placeholder="ticker…"
              onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applySymbol(symbolInput);
              }}
              onBlur={() => applySymbol(symbolInput)}
            />
          </div>
        </div>
        <div className="fg fmquick">
          <span className="lab">Quick</span>
          <div className="ctl">
            {QUICK_SYMBOLS.filter((sym) => !quickClass || classifyTicker(sym) === quickClass).map((s) => (
              <button
                key={s}
                type="button"
                className={`chip${symbol === s ? ' on' : ''}`}
                onClick={() => applySymbol(s)}
                onMouseEnter={() => prefetchLadder({ symbol: s, dte, strikeRange })}
                onFocus={() => prefetchLadder({ symbol: s, dte, strikeRange })}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Type</span>
          <div className="ctl">
            {ASSET_CLASS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip${quickClass === o.value ? ' on' : ''}`}
                onClick={() => setQuickClass(quickClass === o.value ? '' : o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Expiry</span>
          <div className="ctl">
            {MATRIX_DTE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip${dte === o.value ? ' on' : ''}`}
                onClick={() => setDte(o.value)}
                onMouseEnter={() => prefetchLadder({ symbol, dte: o.value, strikeRange })}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Strikes</span>
          <div className="ctl">
            {MATRIX_STRIKE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip${strikeRange === o.value ? ' on' : ''}`}
                onClick={() => setStrikeRange(o.value)}
                onMouseEnter={() => prefetchLadder({ symbol, dte, strikeRange: o.value })}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Metric</span>
          <div className="ctl">
            <select className="sel" value={metric} onChange={(e) => setMetric(e.target.value)}>
              {SINGLE_METRICS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {totals && (
        <div className="fmtotals">
          <div className="fmtotal call">
            <span className="k">Calls</span>
            <span className="v mono">{def.format(totals.call)}</span>
          </div>
          <div className="fmtotal put">
            <span className="k">Puts</span>
            <span className="v mono">{def.format(totals.put)}</span>
          </div>
          <div className="fmtotal">
            <span className="k">Put / Call</span>
            <span className="v mono">{totals.ratio == null ? '—' : totals.ratio.toFixed(2)}</span>
          </div>
          <div className="fmtotal">
            <span className="k">Metric</span>
            <span className="v">{def.label}</span>
          </div>
        </div>
      )}

      {error && (ds ? <ToolState kind="error" lang={isArabic ? 'ar' : 'en'} title={error} body="" /> : <div className="ftError">{error}</div>)}

      <div className="fmladder" ref={bodyRef}>
        <div className="fmhead">
          <span className="fmcell fmside call">Calls · {def.label}</span>
          <span className="fmcell fmstrike">Strike</span>
          <span className="fmcell fmside put">Puts · {def.label}</span>
        </div>

        {!ladder && loading && (ds ? <ToolState kind="loading" lang={isArabic ? 'ar' : 'en'} rows={10} /> : <div className="empty">Loading chain…</div>)}
        {ds && ladder && !ladder.rows.length && (
          <ToolState
            kind="empty"
            lang={isArabic ? 'ar' : 'en'}
            title="No contracts in this window"
            body={dte === '0dte' ? 'Nothing expires today for this symbol.' : ''}
          />
        )}
        {!ds && ladder && !ladder.rows.length && (
          <div className="empty">
            No contracts in this window
            {dte === '0dte' ? ' — nothing expires today for this symbol.' : '.'}
          </div>
        )}

        {ladder?.rows.map((row, i) => {
          const c = row.call[metric] ?? 0;
          const p = row.put[metric] ?? 0;
          const isAtm = i === atm;
          return (
            <div className={`fmrow${isAtm ? ' atm' : ''}`} key={row.strike} data-row-index={i}>
              <button
                type="button"
                className="fmcell fmside call"
                title={`${symbol} ${row.strike} call — open contract history`}
                onClick={() => openContract(row.strike, row.expiry, 'call')}
              >
                <span className="fmbar call" style={{ width: `${barPct(c)}%` }} />
                <span className="fmval mono">{c ? def.format(c) : ''}</span>
              </button>
              <span className="fmcell fmstrike mono">{row.strike}</span>
              <button
                type="button"
                className="fmcell fmside put"
                title={`${symbol} ${row.strike} put — open contract history`}
                onClick={() => openContract(row.strike, row.expiry, 'put')}
              >
                <span className="fmbar put" style={{ width: `${barPct(p)}%` }} />
                <span className="fmval mono">{p ? def.format(p) : ''}</span>
              </button>
            </div>
          );
        })}
      </div>

      <div className="note">
        Bars scale to the largest {def.label.toLowerCase()} on this ladder, so both sides read on one
        scale. The highlighted row is the strike nearest spot. Click either side to open that
        contract&apos;s history. Changing expiry narrows the request server-side, and neighbouring
        windows are prefetched as you hover.
      </div>
    </div>
  );
};

/**
 * The tab: the single ladder by default; compare mode once the user adds a pane. The compare
 * layout lives under its own storage key, so the single-pane prefs (and anything reading them)
 * never see compare-only values such as live-feed metrics.
 */
const FlowMatrixView: React.FC<FlowMatrixViewProps> = ({ simpleLayout = false }) => {
  const [compare, setCompare] = useState<CompareState | null>(() => {
    const s = loadCompareState();
    return s && s.open ? s : null;
  });

  const update = useCallback((next: CompareState) => {
    setCompare(next);
    saveCompareState(next);
  }, []);

  const start = useCallback(
    (cur: Prefs) => {
      const saved = loadCompareState();
      if (saved) {
        update({ ...saved, open: true });
        return;
      }
      const a = { id: newPaneId(), symbol: cur.symbol, source: 'stable' as const, dte: cur.dte, strikeRange: cur.strikeRange, metric: cur.metric, liveWindow: 'session' as const };
      // Second pane starts as the same underlying on a different expiry window: the most common comparison.
      const b = { ...a, id: newPaneId(), dte: cur.dte === '0dte' ? 'weekly' : '0dte' };
      update({ open: true, panes: [a, b], sync: false, align: true, diff: false, diffPair: [0, 1] });
    },
    [update],
  );

  const exit = useCallback(() => {
    if (compare) saveCompareState({ ...compare, open: false });
    setCompare(null);
  }, [compare]);

  if (compare) {
    return <FlowMatrixCompare state={compare} onChange={update} onExit={exit} simpleLayout={simpleLayout} />;
  }
  return <FlowMatrixSingle simpleLayout={simpleLayout} onCompare={start} />;
};

export default FlowMatrixView;
